import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';

export interface OpportunityContact {
  id: string;
  contact_id: string;
  position: number;
  contact: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    org_id: string | null;
  };
}

export interface OpportunityLocation {
  id: string;
  interest_level: 'interested' | 'high_priority' | 'committed';
  location: {
    id: string;
    name: string;
    city: string | null;
  } | null;
}

export interface Opportunity {
  id: string;
  org_id: string | null;
  contact_id: string | null;
  name: string;
  status: string;
  total_fleet: number | null;
  electric_trucks: number | null;
  description: string | null;
  tags: string[];
  tenant_id: string;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  organization?: {
    id: string;
    name: string;
    website: string | null;
    favicon_url: string | null;
    logo_url: string | null;
    cached_favicon_url: string | null;
  } | null;
  contact?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
    email: string | null;
    org_id: string | null;
  } | null;
  opportunity_contacts?: OpportunityContact[];
  opportunity_locations?: OpportunityLocation[];
  notes_count?: number;
}

export interface OpportunityNote {
  id: string;
  opportunity_id: string;
  body: string;
  note_type: string;
  note_date: string | null;
  created_by: string | null;
  created_at: string;
  profile?: {
    display_name: string | null;
    email: string | null;
  } | null;
}

export const OPPORTUNITY_STATUSES = [
  { id: 'prospect', label: 'Prospect', color: 'hsl(var(--primary))' },
  { id: 'in_gesprek', label: 'In Gesprek', color: 'hsl(217, 91%, 60%)' },
  { id: 'offerte', label: 'Offerte', color: 'hsl(38, 92%, 50%)' },
  { id: 'onderhandeling', label: 'Onderhandeling', color: 'hsl(280, 87%, 57%)' },
  { id: 'gewonnen', label: 'Gewonnen', color: 'hsl(142, 76%, 36%)' },
  { id: 'verloren', label: 'Verloren', color: 'hsl(0, 84%, 60%)' },
];

export const NOTE_TYPES = [
  { id: 'notitie', label: 'Notitie', icon: 'FileText' },
  { id: 'telefoongesprek', label: 'Telefoongesprek', icon: 'Phone' },
  { id: 'meeting', label: 'Meeting', icon: 'Users' },
  { id: 'email', label: 'Email', icon: 'Mail' },
  { id: 'presentatie', label: 'Presentatie', icon: 'Presentation' },
];

export function useOpportunities() {
  const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchOpportunities = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('charging_opportunities')
        .select(`
          *,
          organization:organizations(id, name, website, favicon_url, logo_url, cached_favicon_url),
          contact:contacts(id, first_name, last_name, email, org_id),
          opportunity_contacts(
            id,
            contact_id,
            position,
            contact:contacts(id, first_name, last_name, email, org_id)
          ),
          opportunity_locations(
            id,
            interest_level,
            location:charging_locations(id, name, city)
          )
        `)
        .is('deleted_at', null)
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      // Fetch notes count for each opportunity
      const opportunitiesWithCounts = await Promise.all(
        (data || []).map(async (opp) => {
          const { count } = await supabase
            .from('opportunity_notes')
            .select('*', { count: 'exact', head: true })
            .eq('opportunity_id', opp.id);
          return { ...opp, notes_count: count || 0 };
        })
      );
      
      setOpportunities(opportunitiesWithCounts as Opportunity[]);
    } catch (error: any) {
      console.error('Error fetching opportunities:', error);
      toast({
        title: 'Fout bij ophalen',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createOpportunity = async (data: Partial<Opportunity> & { contact_ids?: string[] }) => {
    try {
      const { data: profile } = await supabase
        .from('profiles')
        .select('tenant_id')
        .eq('id', user?.id)
        .single();

      const { contact_ids, ...oppData } = data;

      const insertData = {
        name: oppData.name || '',
        org_id: oppData.org_id || null,
        contact_id: oppData.contact_id || null,
        status: oppData.status || 'prospect',
        total_fleet: oppData.total_fleet || null,
        electric_trucks: oppData.electric_trucks || null,
        description: oppData.description || null,
        tags: oppData.tags || [],
        tenant_id: profile?.tenant_id || user?.id || '',
        created_by: user?.id || null,
      };

      const { data: newOpp, error } = await supabase
        .from('charging_opportunities')
        .insert(insertData)
        .select()
        .single();

      if (error) throw error;

      // Insert contact relationships if provided
      if (contact_ids && contact_ids.length > 0 && newOpp) {
        const contactInserts = contact_ids.map((contact_id, index) => ({
          opportunity_id: newOpp.id,
          contact_id,
          position: index + 1,
        }));

        await supabase.from('opportunity_contacts').insert(contactInserts);
      }

      toast({ title: 'Opportunity aangemaakt' });
      await fetchOpportunities();
      return newOpp;
    } catch (error: any) {
      console.error('Error creating opportunity:', error);
      toast({
        title: 'Fout bij aanmaken',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateOpportunity = async (id: string, data: Partial<Opportunity> & { contact_ids?: string[] }) => {
    try {
      const { contact_ids, ...oppData } = data;

      const { error } = await supabase
        .from('charging_opportunities')
        .update(oppData)
        .eq('id', id);

      if (error) throw error;

      // Update contacts if provided
      if (contact_ids !== undefined) {
        // Remove existing contacts
        await supabase.from('opportunity_contacts').delete().eq('opportunity_id', id);

        // Insert new contacts
        if (contact_ids.length > 0) {
          const contactInserts = contact_ids.map((contact_id, index) => ({
            opportunity_id: id,
            contact_id,
            position: index + 1,
          }));

          await supabase.from('opportunity_contacts').insert(contactInserts);
        }
      }

      toast({ title: 'Opportunity bijgewerkt' });
      await fetchOpportunities();
    } catch (error: any) {
      console.error('Error updating opportunity:', error);
      toast({
        title: 'Fout bij bijwerken',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const deleteOpportunity = async (id: string) => {
    try {
      const { error } = await supabase
        .from('charging_opportunities')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', id);

      if (error) throw error;

      toast({ title: 'Opportunity verwijderd' });
      await fetchOpportunities();
    } catch (error: any) {
      console.error('Error deleting opportunity:', error);
      toast({
        title: 'Fout bij verwijderen',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchOpportunities();
  }, []);

  return {
    opportunities,
    loading,
    refetch: fetchOpportunities,
    createOpportunity,
    updateOpportunity,
    deleteOpportunity,
  };
}

export function useOpportunityNotes(opportunityId: string) {
  const [notes, setNotes] = useState<OpportunityNote[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const { user } = useAuth();

  const fetchNotes = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('opportunity_notes')
        .select(`
          *,
          profile:profiles!opportunity_notes_created_by_fkey(display_name, email)
        `)
        .eq('opportunity_id', opportunityId)
        .order('note_date', { ascending: false })
        .order('created_at', { ascending: false });

      if (error) throw error;
      setNotes(data as OpportunityNote[]);
    } catch (error: any) {
      console.error('Error fetching notes:', error);
    } finally {
      setLoading(false);
    }
  };

  const addNote = async (body: string, noteType: string, noteDate?: Date) => {
    try {
      const { error } = await supabase
        .from('opportunity_notes')
        .insert({
          opportunity_id: opportunityId,
          body,
          note_type: noteType,
          note_date: noteDate ? noteDate.toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          created_by: user?.id,
        });

      if (error) throw error;

      toast({ title: 'Notitie toegevoegd' });
      await fetchNotes();
    } catch (error: any) {
      console.error('Error adding note:', error);
      toast({
        title: 'Fout bij toevoegen',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const updateNote = async (id: string, body: string) => {
    try {
      const { error } = await supabase
        .from('opportunity_notes')
        .update({ body })
        .eq('id', id);

      if (error) throw error;

      toast({ title: 'Notitie bijgewerkt' });
      await fetchNotes();
    } catch (error: any) {
      console.error('Error updating note:', error);
      toast({
        title: 'Fout bij bijwerken',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const deleteNote = async (id: string) => {
    try {
      const { error } = await supabase
        .from('opportunity_notes')
        .delete()
        .eq('id', id);

      if (error) throw error;

      toast({ title: 'Notitie verwijderd' });
      await fetchNotes();
    } catch (error: any) {
      console.error('Error deleting note:', error);
      toast({
        title: 'Fout bij verwijderen',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    if (opportunityId) {
      fetchNotes();
    }
  }, [opportunityId]);

  return {
    notes,
    loading,
    refetch: fetchNotes,
    addNote,
    updateNote,
    deleteNote,
  };
}
